import { useEffect, useMemo, useState } from 'react'
import MessageBox from '../components/MessageBox'
import EditButton from '../components/EditButtonModal'
import {
  MenuItem,
  TableSortLabel,
  TextField,
  Toolbar
} from '@mui/material'
import {
  fetchWithAuth,
  invalidateFetchCache
} from '../utils/fetchWithAuth'

const DISPLAY_PLATFORM_OPTIONS = [
  { value: 'both', label: 'Web aj mobil' },
  { value: 'web', label: 'Iba web' },
  { value: 'mobile', label: 'Iba mobilná appka' }
]

const PLACEMENT_OPTIONS = [
  'home',
  'index',
  'check_vehicle',
  'PZP_check'
]

const ACTION_TYPE_OPTIONS = [
  'web',
  'modal'
]

const LANGUAGE_OPTIONS = [
  { value: '', label: 'Všetky' },
  { value: 'sk', label: 'SK' },
  { value: 'en', label: 'EN' },
  { value: 'cs', label: 'CS' },
  { value: 'hu', label: 'HU' },
  { value: 'pl', label: 'PL' },
  { value: 'de', label: 'DE' },
  { value: 'ua', label: 'UA' },
  { value: 'fr', label: 'FR' },
  { value: 'es', label: 'ES' },
  { value: 'et', label: 'ET' }
]

const normalizeDisplayPlatform = (value) => {
  return DISPLAY_PLATFORM_OPTIONS.some(
    option => option.value === value
  )
    ? value
    : 'both'
}

const normalizeCountryCode = (value) => {
  if (typeof value !== 'string') return ''

  const normalized =
    value.trim().toUpperCase()

  return normalized === 'NULL'
    ? ''
    : normalized
}

const normalizePlacement = (value) => {
  if (typeof value !== 'string') return ''

  const normalized = value.trim()

  return normalized.toLowerCase() === 'null'
    ? ''
    : normalized
}

const normalizeLanguage = (value) => {
  if (typeof value !== 'string') return ''

  const normalized =
    value.trim().toLowerCase()

  return normalized === 'null'
    ? ''
    : normalized
}

const toDateTimeLocal = (value) => {
  if (!value) return ''

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const pad = (number) =>
    String(number).padStart(2, '0')

  return (
    `${date.getFullYear()}-` +
    `${pad(date.getMonth() + 1)}-` +
    `${pad(date.getDate())}T` +
    `${pad(date.getHours())}:` +
    `${pad(date.getMinutes())}`
  )
}

const formatDate = (value) => {
  if (!value) return '-'

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  return date.toLocaleString()
}

const AdminPromoBanners = () => {
  const [banners, setBanners] = useState([])
  const [countries, setCountries] = useState([])

  const [filterText, setFilterText] = useState('')
  const [placementFilter, setPlacementFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [validFromFilter, setValidFromFilter] = useState('')
  const [validToFilter, setValidToFilter] = useState('')
  const [rotateSecondsFilter, setRotateSecondsFilter] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [languageFilter, setLanguageFilter] = useState('')
  const [orderBy, setOrderBy] = useState('id')
  const [order, setOrder] = useState('asc')

  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState(null)
  const [message, setMessage] = useState(null)

  const [editId, setEditId] = useState(null)
  const [editForm, setEditForm] = useState(null)

  const [hoverText, setHoverText] = useState(null)
  const [hoverPosition, setHoverPosition] = useState({
    x: 0,
    y: 0
  })

  const loadBanners = async () => {
    setLoading(true)

    try {
      const res = await fetchWithAuth(
        '/admin/promo-banners',
        {
          cacheTtlMs: 0
        }
      )

      const data = await res.json()

      if (!res.ok) {
        throw new Error(
          data?.message ||
          'Nepodarilo sa načítať promobanery'
        )
      }

      setBanners(
        Array.isArray(data?.banners)
          ? data.banners
          : []
      )
    } catch (err) {
      setMessage({
        type: 'error',
        message:
          err.message ||
          'Nepodarilo sa načítať promobanery'
      })
    } finally {
      setLoading(false)
    }
  }

  const loadCountries = async () => {
    try {
      const res = await fetchWithAuth(
        '/admin/countries',
        {
          cacheTtlMs: 0
        }
      )

      const data = await res.json()

      if (!res.ok) {
        throw new Error(
          data?.message ||
          'Nepodarilo sa načítať krajiny'
        )
      }

      setCountries(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (err) {
      setMessage({
        type: 'error',
        message:
          err.message ||
          'Nepodarilo sa načítať krajiny'
      })
    }
  }

  useEffect(() => {
    loadBanners()
    loadCountries()
  }, [])

  const columns = useMemo(
    () => [
      {
        id: 'id',
        get: banner => banner.id
      },
      {
        id: 'title',
        get: banner => banner.title || ''
      },
      {
        id: 'placement',
        get: banner => banner.placement || ''
      },
      {
        id: 'type',
        get: banner => banner.type || ''
      },
      {
        id: 'description',
        get: banner => banner.description || ''
      },
      {
        id: 'image_url',
        get: banner => banner.image_url || ''
      },
      {
        id: 'video_url',
        get: banner => banner.video_url || ''
      },
      {
        id: 'target_url',
        get: banner => banner.target_url || ''
      },
      {
        id: 'active',
        get: banner =>
          banner.active
            ? 'Áno'
            : 'Nie'
      },
      {
        id: 'valid_from',
        get: banner => banner.valid_from || ''
      },
      {
        id: 'valid_to',
        get: banner => banner.valid_to || ''
      },
      {
        id: 'rotate_seconds',
        get: banner => banner.rotate_seconds ?? ''
      },
      {
        id: 'country_code',
        get: banner => banner.country_code || ''
      },
      {
        id: 'action_type',
        get: banner => banner.action_type || ''
      },
      {
        id: 'purchased_impressions',
        get: banner => banner.purchased_impressions ?? ''
      },
      {
        id: 'displayed_impressions',
        get: banner => banner.displayed_impressions ?? 0
      },
      {
        id: 'clicks',
        get: banner => banner.clicks ?? 0
      },
      {
        id: 'display_platform',
        get: banner =>
          normalizeDisplayPlatform(
            banner.display_platform
          )
      },
      {
        id: 'lang',
        get: banner => banner.lang || ''
      },
      {
        id: 'last_served_web_at',
        get: banner => banner.last_served_web_at || ''
      },
      {
        id: 'last_served_mobile_at',
        get: banner => banner.last_served_mobile_at || ''
      },
      {
        id: 'created_at',
        get: banner =>
          banner.created_at ??
          banner.createdAt ??
          ''
      },
      {
        id: 'updated_at',
        get: banner =>
          banner.updated_at ??
          banner.updatedAt ??
          ''
      }
    ],
    []
  )

  const processedBanners = useMemo(() => {
    const q =
      filterText
        .trim()
        .toLowerCase()

    const filtered = banners.filter(banner => {
      if (q) {
        const values = [
          banner.id,
          banner.title,
          banner.type,
          banner.description,
          banner.image_url,
          banner.video_url,
          banner.target_url,
          banner.action_type,
          banner.purchased_impressions,
          banner.displayed_impressions,
          banner.clicks,
          normalizeDisplayPlatform(
            banner.display_platform
          ),
          banner.last_served_web_at,
          banner.last_served_mobile_at,
          banner.created_at ??
            banner.createdAt,
          banner.updated_at ??
            banner.updatedAt
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        if (!values.includes(q)) {
          return false
        }
      }

      if (
        placementFilter &&
        normalizePlacement(
          banner.placement
        ) !== placementFilter
      ) {
        return false
      }

      if (
        activeFilter &&
        String(
          banner.active !== false
            ? '1'
            : '0'
        ) !== activeFilter
      ) {
        return false
      }

      if (
        validFromFilter &&
        toDateTimeLocal(
          banner.valid_from
        ).slice(0, 10) !== validFromFilter
      ) {
        return false
      }

      if (
        validToFilter &&
        toDateTimeLocal(
          banner.valid_to
        ).slice(0, 10) !== validToFilter
      ) {
        return false
      }

      if (
        rotateSecondsFilter !== '' &&
        String(
          banner.rotate_seconds ?? ''
        ) !== rotateSecondsFilter
      ) {
        return false
      }

      if (
        countryFilter &&
        normalizeCountryCode(
          banner.country_code
        ) !== countryFilter
      ) {
        return false
      }

      if (
        languageFilter &&
        normalizeLanguage(
          banner.lang
        ) !== languageFilter
      ) {
        return false
      }

      return true
    })

    const col =
      columns.find(
        column => column.id === orderBy
      ) ||
      columns[0]

    const withValue =
      filtered.map(banner => ({
        banner,
        value: col.get(banner)
      }))

    withValue.sort((a, b) => {
      const av = a.value ?? ''
      const bv = b.value ?? ''

      const an =
        typeof av === 'number'
          ? av
          : Number.isFinite(+av) && av !== ''
            ? +av
            : null

      const bn =
        typeof bv === 'number'
          ? bv
          : Number.isFinite(+bv) && bv !== ''
            ? +bv
            : null

      let cmp = 0

      if (
        an !== null &&
        bn !== null
      ) {
        cmp = an - bn
      } else {
        cmp = String(av).localeCompare(
          String(bv),
          'sk',
          {
            sensitivity: 'base',
            numeric: true
          }
        )
      }

      return order === 'asc'
        ? cmp
        : -cmp
    })

    return withValue.map(
      item => item.banner
    )
  }, [
    banners,
    filterText,
    placementFilter,
    activeFilter,
    validFromFilter,
    validToFilter,
    rotateSecondsFilter,
    countryFilter,
    languageFilter,
    orderBy,
    order,
    columns
  ])

  const handleSort = (id) => {
    if (orderBy === id) {
      setOrder(prev =>
        prev === 'asc'
          ? 'desc'
          : 'asc'
      )
    } else {
      setOrderBy(id)
      setOrder('asc')
    }
  }

  const sortHeader = (
    id,
    label
  ) => (
    <th style={styles.th}>
      <TableSortLabel
        active={orderBy === id}
        direction={
          orderBy === id
            ? order
            : 'asc'
        }
        onClick={() =>
          handleSort(id)
        }
        sx={{
          fontSize: 13,
          fontWeight: 700
        }}
      >
        {label}
      </TableSortLabel>
    </th>
  )

  const handleEdit = (banner) => {
    setEditId(banner.id)

    setEditForm({
      placement:
        normalizePlacement(banner.placement),

      type:
        banner.type ?? '',

      title:
        banner.title ?? '',

      description:
        banner.description ?? '',

      image_url:
        banner.image_url ?? '',

      video_url:
        banner.video_url ?? '',

      target_url:
        banner.target_url ?? '',

      active:
        banner.active !== false,

      valid_from:
        toDateTimeLocal(banner.valid_from),

      valid_to:
        toDateTimeLocal(banner.valid_to),

      rotate_seconds:
        String(
          banner.rotate_seconds ?? ''
        ),

      country_code:
        normalizeCountryCode(
          banner.country_code
        ),

      action_type:
        banner.action_type ?? '',

      purchased_impressions:
        banner.purchased_impressions === null ||
        banner.purchased_impressions === undefined
          ? ''
          : String(
              banner.purchased_impressions
            ),

      display_platform:
        normalizeDisplayPlatform(
          banner.display_platform
        ),

      lang:
        normalizeLanguage(banner.lang)
    })
  }

  const handleCancelEdit = () => {
    setEditId(null)
    setEditForm(null)
  }

  const handleSaveEdit = async (bannerId) => {
    if (!editForm) return

    setSavingId(bannerId)
    setMessage(null)

    try {
      const payload = {
        ...editForm,

        placement:
          normalizePlacement(
            editForm.placement
          ) || null,

        country_code:
          normalizeCountryCode(
            editForm.country_code
          ) || null,

        lang:
          normalizeLanguage(
            editForm.lang
          ) || null,

        description:
          editForm.description.trim() || null,

        image_url:
          editForm.image_url.trim() || null,

        video_url:
          editForm.video_url.trim() || null,

        target_url:
          editForm.target_url.trim() || null,

        valid_from:
          editForm.valid_from
            ? new Date(
                editForm.valid_from
              ).toISOString()
            : null,

        valid_to:
          editForm.valid_to
            ? new Date(
                editForm.valid_to
              ).toISOString()
            : null,

        rotate_seconds:
          Number(
            editForm.rotate_seconds
          ),

        purchased_impressions:
          editForm.purchased_impressions === ''
            ? null
            : Number(
                editForm.purchased_impressions
              )
      }

      const res = await fetchWithAuth(
        `/admin/promo-banners/${bannerId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type':
              'application/json'
          },
          body: JSON.stringify(payload)
        }
      )

      const data = await res.json()

      if (!res.ok) {
        throw new Error(
          data?.message ||
          'Nepodarilo sa uložiť promobanner'
        )
      }

      setEditId(null)
      setEditForm(null)

      invalidateFetchCache(
        '/promo-banners'
      )

      await loadBanners()

      setMessage({
        type: 'success',
        message:
          'Promobanner bol uložený'
      })
    } catch (err) {
      setMessage({
        type: 'error',
        message:
          err.message ||
          'Nepodarilo sa uložiť promobanner'
      })
    } finally {
      setSavingId(null)
    }
  }

  const editInput = (
    key,
    type = 'text',
    extraProps = {}
  ) => (
    <input
      type={type}
      value={editForm?.[key] ?? ''}
      onChange={(e) =>
        setEditForm(prev => ({
          ...prev,
          [key]: e.target.value
        }))
      }
      {...extraProps}
    />
  )

  const editTextarea = (key) => (
    <textarea
      value={editForm?.[key] ?? ''}
      onChange={(e) =>
        setEditForm(prev => ({
          ...prev,
          [key]: e.target.value
        }))
      }
      style={styles.editTextarea}
    />
  )

  const showHoverText = (e, text) => {
    if (!text) return

    const rect =
      e.currentTarget.getBoundingClientRect()

    setHoverText(text)

    setHoverPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 6
    })
  }

  const hideHoverText = () => {
    setHoverText(null)
  }

  const shortText = (text) => (
    <div
      style={styles.shortText}
      onMouseEnter={(e) =>
        showHoverText(e, text)
      }
      onMouseLeave={hideHoverText}
    >
      {text || '-'}
    </div>
  )

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={styles.headerRow}>
        <div>
          <h3 style={styles.title}>
            Promobanery
          </h3>

          <p style={styles.subtitle}>
            Nastav, či sa má banner zobrazovať kamkoľvek,
            na konkrétnom umiestnení, na webe,
            v mobilnej appke alebo na oboch platformách.
          </p>
        </div>
      </div>

      <Toolbar
        disableGutters
        sx={{
          minHeight: '42px !important',
          marginBottom: 1
        }}
      >
        <TextField
          value={filterText}
          onChange={e =>
            setFilterText(
              e.target.value
            )
          }
          size="small"
          label="Filtrovať (full-text)"
          placeholder="Hľadať v promobanneroch…"
          sx={{
            width: 400,
            maxWidth: '100%',
            '& .MuiInputBase-root': {
              fontSize: 13
            },
            '& .MuiInputLabel-root': {
              fontSize: 13
            }
          }}
        />
      </Toolbar>

      <Toolbar
        disableGutters
        sx={{
          minHeight: '42px !important',
          marginBottom: 1,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap'
        }}
      >
        <TextField
          select
          value={placementFilter}
          onChange={e =>
            setPlacementFilter(
              e.target.value
            )
          }
          size="small"
          label="Umiestnenie"
          sx={styles.filterSelect}
        >
          <MenuItem value="">
            Všetky
          </MenuItem>

          {PLACEMENT_OPTIONS.map(
            placement => (
              <MenuItem
                key={placement}
                value={placement}
              >
                {placement}
              </MenuItem>
            )
          )}
        </TextField>

        <TextField
          select
          value={activeFilter}
          onChange={e =>
            setActiveFilter(
              e.target.value
            )
          }
          size="small"
          label="Aktívny"
          sx={styles.filterSelect}
        >
          <MenuItem value="">
            Všetky
          </MenuItem>

          <MenuItem value="1">
            Áno
          </MenuItem>

          <MenuItem value="0">
            Nie
          </MenuItem>
        </TextField>

        <TextField
          type="date"
          value={validFromFilter}
          onChange={e =>
            setValidFromFilter(
              e.target.value
            )
          }
          size="small"
          label="Platí od"
          InputLabelProps={{
            shrink: true
          }}
          sx={styles.filterDate}
        />

        <TextField
          type="date"
          value={validToFilter}
          onChange={e =>
            setValidToFilter(
              e.target.value
            )
          }
          size="small"
          label="Platí do"
          InputLabelProps={{
            shrink: true
          }}
          sx={styles.filterDate}
        />

        <TextField
          type="number"
          value={rotateSecondsFilter}
          onChange={e =>
            setRotateSecondsFilter(
              e.target.value
            )
          }
          size="small"
          label="Rotácia (sekundy)"
          inputProps={{
            min: 0
          }}
          sx={styles.filterNumber}
        />

        <TextField
          select
          value={countryFilter}
          onChange={e =>
            setCountryFilter(
              e.target.value
            )
          }
          size="small"
          label="Cieľová krajina"
          sx={styles.filterCountry}
        >
          <MenuItem value="">
            Všetky
          </MenuItem>

          {countries.map(
            country => (
              <MenuItem
                key={country.code}
                value={
                  normalizeCountryCode(
                    country.code
                  )
                }
              >
                {country.code} - {country.name}
              </MenuItem>
            )
          )}
        </TextField>

        <TextField
          select
          value={languageFilter}
          onChange={e =>
            setLanguageFilter(
              e.target.value
            )
          }
          size="small"
          label="Jazyk"
          sx={styles.filterLanguage}
        >
          {LANGUAGE_OPTIONS.map(
            option => (
              <MenuItem
                key={
                  option.value ||
                  'all'
                }
                value={option.value}
              >
                {option.label}
              </MenuItem>
            )
          )}
        </TextField>
      </Toolbar>

      {message && (
        <MessageBox
          message={message.message}
          type={message.type}
        />
      )}

      {loading ? (
        <p>Načítavam...</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRowTable}>
                <th style={styles.th}>Akcie</th>
                {sortHeader('id', 'ID')}
                {sortHeader('title', 'Názov')}
                {sortHeader('placement', 'Umiestnenie')}
                {sortHeader('type', 'Typ')}
                {sortHeader('description', 'Popis')}
                {sortHeader('image_url', 'Image URL')}
                {sortHeader('video_url', 'Video URL')}
                {sortHeader('target_url', 'Target URL')}
                {sortHeader('active', 'Aktívny')}
                {sortHeader('valid_from', 'Platí od')}
                {sortHeader('valid_to', 'Platí do')}
                {sortHeader('rotate_seconds', 'Rotácia (sekundy)')}
                {sortHeader('country_code', 'Cieľová krajina')}
                {sortHeader('action_type', 'Akcia po kliknutí')}
                {sortHeader('purchased_impressions', 'Zakúpené zobrazenia')}
                {sortHeader('displayed_impressions', 'Zobrazené')}
                {sortHeader('clicks', 'Kliknutia')}
                {sortHeader('display_platform', 'Zobraziť na')}
                {sortHeader('lang', 'Jazyk')}
                {sortHeader('last_served_web_at', 'Web naposledy')}
                {sortHeader('last_served_mobile_at', 'Mobil naposledy')}
                {sortHeader('created_at', 'Vytvorené')}
                {sortHeader('updated_at', 'Upravené')}
              </tr>
            </thead>

            <tbody>
              {processedBanners.map(banner => {
                const isEditing =
                  editId === banner.id

                return (
                  <tr key={banner.id}>
                    <td style={styles.td}>
                      {isEditing ? (
                        <>
                          <button
                            disabled={
                              savingId ===
                              banner.id
                            }
                            onClick={() =>
                              handleSaveEdit(
                                banner.id
                              )
                            }
                          >
                            Uložiť
                          </button>

                          <button
                            disabled={
                              savingId ===
                              banner.id
                            }
                            onClick={
                              handleCancelEdit
                            }
                          >
                            Zrušiť
                          </button>
                        </>
                      ) : (
                        <EditButton
                          onClick={() =>
                            handleEdit(
                              banner
                            )
                          }
                        />
                      )}
                    </td>

                    <td style={styles.td}>
                      {banner.id}
                    </td>

                    <td style={styles.td}>
                      {isEditing
                        ? editInput('title')
                        : banner.title || '-'}
                    </td>

                    <td
                      style={{
                        ...styles.td,
                        ...styles.narrowTd
                      }}
                    >
                      {isEditing ? (
                        <select
                          value={
                            editForm.placement
                          }
                          onChange={(e) =>
                            setEditForm(
                              prev => ({
                                ...prev,
                                placement:
                                  e.target.value
                              })
                            )
                          }
                          style={styles.narrowSelect}
                        >
                          {PLACEMENT_OPTIONS.map(
                            placement => (
                              <option
                                key={placement}
                                value={placement}
                              >
                                {placement}
                              </option>
                            )
                          )}
                        </select>
                      ) : (
                        banner.placement || '-'
                      )}
                    </td>

                    <td style={styles.td}>
                      {isEditing ? (
                        <select
                          value={editForm.type}
                          onChange={(e) =>
                            setEditForm(
                              prev => ({
                                ...prev,
                                type:
                                  e.target.value
                              })
                            )
                          }
                        >
                          <option value="text">
                            text
                          </option>

                          <option value="image">
                            image
                          </option>

                          <option value="video">
                            video
                          </option>
                        </select>
                      ) : (
                        banner.type || '-'
                      )}
                    </td>

                    <td style={styles.td}>
                      {isEditing
                        ? editTextarea(
                            'description'
                          )
                        : shortText(
                            banner.description
                          )}
                    </td>

                    <td style={styles.td}>
                      {isEditing
                        ? editTextarea(
                            'image_url'
                          )
                        : shortText(
                            banner.image_url
                          )}
                    </td>

                    <td style={styles.td}>
                      {isEditing
                        ? editTextarea(
                            'video_url'
                          )
                        : shortText(
                            banner.video_url
                          )}
                    </td>

                    <td style={styles.td}>
                      {isEditing
                        ? editTextarea(
                            'target_url'
                          )
                        : shortText(
                            banner.target_url
                          )}
                    </td>

                    <td style={styles.td}>
                      {isEditing ? (
                        <select
                          value={
                            editForm.active
                              ? '1'
                              : '0'
                          }
                          onChange={(e) =>
                            setEditForm(
                              prev => ({
                                ...prev,
                                active:
                                  e.target.value ===
                                  '1'
                              })
                            )
                          }
                        >
                          <option value="1">
                            Áno
                          </option>

                          <option value="0">
                            Nie
                          </option>
                        </select>
                      ) : (
                        banner.active
                          ? 'Áno'
                          : 'Nie'
                      )}
                    </td>

                    <td style={styles.td}>
                      {isEditing
                        ? editInput(
                            'valid_from',
                            'datetime-local'
                          )
                        : formatDate(
                            banner.valid_from
                          )}
                    </td>

                    <td style={styles.td}>
                      {isEditing
                        ? editInput(
                            'valid_to',
                            'datetime-local'
                          )
                        : formatDate(
                            banner.valid_to
                          )}
                    </td>

                    <td
                      style={{
                        ...styles.td,
                        ...styles.smallTd
                      }}
                    >
                      {isEditing
                        ? editInput(
                            'rotate_seconds',
                            'number',
                            {
                              min: 0,
                              style:
                                styles.smallInput
                            }
                          )
                        : banner.rotate_seconds}
                    </td>

                    <td
                      style={{
                        ...styles.td,
                        ...(isEditing
                          ? {
                              minWidth: 170
                            }
                          : {})
                      }}
                    >
                      {isEditing ? (
                        <div
                          style={
                            styles.countrySelectWrap
                          }
                        >
                          <select
                            value={
                              editForm.country_code
                            }
                            onChange={(e) =>
                              setEditForm(
                                prev => ({
                                  ...prev,
                                  country_code:
                                    e.target.value
                                })
                              )
                            }
                            style={
                              styles.countrySelect
                            }
                          >
                            <option value="">
                              Všetky
                            </option>

                            {countries.map(
                              country => (
                                <option
                                  key={
                                    country.code
                                  }
                                  value={
                                    country.code
                                  }
                                >
                                  {country.code} - {country.name}
                                </option>
                              )
                            )}
                          </select>

                          <span
                            style={
                              styles.countrySelectArrow
                            }
                          >
                            ▼
                          </span>
                        </div>
                      ) : (
                        banner.country_code ||
                        '-'
                      )}
                    </td>

                    <td
                      style={{
                        ...styles.td,
                        ...(isEditing
                          ? {
                              minWidth: 130
                            }
                          : {})
                      }}
                    >
                      {isEditing ? (
                        <select
                          value={
                            editForm.action_type
                          }
                          onChange={(e) =>
                            setEditForm(
                              prev => ({
                                ...prev,
                                action_type:
                                  e.target.value
                              })
                            )
                          }
                          style={
                            styles.actionSelect
                          }
                        >
                          {ACTION_TYPE_OPTIONS.map(
                            action => (
                              <option
                                key={action}
                                value={action}
                              >
                                {action}
                              </option>
                            )
                          )}
                        </select>
                      ) : (
                        banner.action_type ||
                        '-'
                      )}
                    </td>

                    <td style={styles.td}>
                      {isEditing
                        ? editInput(
                            'purchased_impressions',
                            'number',
                            { min: 0 }
                          )
                        : banner.purchased_impressions ??
                          '-'}
                    </td>

                    <td style={styles.td}>
                      {banner.displayed_impressions ??
                        0}
                    </td>

                    <td style={styles.td}>
                      {banner.clicks ?? 0}
                    </td>

                    <td style={styles.td}>
                      {isEditing ? (
                        <select
                          value={
                            editForm.display_platform
                          }
                          onChange={(e) =>
                            setEditForm(
                              prev => ({
                                ...prev,
                                display_platform:
                                  e.target.value
                              })
                            )
                          }
                        >
                          {DISPLAY_PLATFORM_OPTIONS.map(
                            option => (
                              <option
                                key={
                                  option.value
                                }
                                value={
                                  option.value
                                }
                              >
                                {option.label}
                              </option>
                            )
                          )}
                        </select>
                      ) : (
                        normalizeDisplayPlatform(
                          banner.display_platform
                        )
                      )}
                    </td>

                    <td
                      style={{
                        ...styles.td,
                        ...styles.smallTd
                      }}
                    >
                      {isEditing ? (
                        <select
                          value={
                            editForm.lang
                          }
                          onChange={(e) =>
                            setEditForm(
                              prev => ({
                                ...prev,
                                lang:
                                  e.target.value
                              })
                            )
                          }
                          style={
                            styles.languageSelect
                          }
                        >
                          {LANGUAGE_OPTIONS.map(
                            option => (
                              <option
                                key={
                                  option.value ||
                                  'all'
                                }
                                value={
                                  option.value
                                }
                              >
                                {option.label}
                              </option>
                            )
                          )}
                        </select>
                      ) : (
                        banner.lang || '-'
                      )}
                    </td>

                    <td style={styles.td}>
                      {formatDate(
                        banner.last_served_web_at
                      )}
                    </td>

                    <td style={styles.td}>
                      {formatDate(
                        banner.last_served_mobile_at
                      )}
                    </td>

                    <td style={styles.td}>
                      {formatDate(
                        banner.created_at ??
                        banner.createdAt
                      )}
                    </td>

                    <td style={styles.td}>
                      {formatDate(
                        banner.updated_at ??
                        banner.updatedAt
                      )}
                    </td>
                  </tr>
                )
              })}

              {!processedBanners.length && (
                <tr>
                  <td
                    style={styles.emptyTd}
                    colSpan={24}
                  >
                    Žiadne promobanery neboli nájdené.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {hoverText && (
        <div
          style={{
            ...styles.hoverBox,
            left: hoverPosition.x,
            top: hoverPosition.y
          }}
        >
          {hoverText}
        </div>
      )}
    </div>
  )
}

const styles = {
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 16
  },

  title: {
    fontSize: 18,
    marginTop: 0,
    marginBottom: 0
  },

  subtitle: {
    margin: '6px 0 0',
    color: '#555'
  },

  filterSelect: {
    minWidth: 140,
    '& .MuiInputBase-root': {
      fontSize: 13
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    }
  },

  filterDate: {
    width: 160,
    '& .MuiInputBase-root': {
      fontSize: 13
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    }
  },

  filterNumber: {
    width: 160,
    '& .MuiInputBase-root': {
      fontSize: 13
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    }
  },

  filterCountry: {
    minWidth: 180,
    '& .MuiInputBase-root': {
      fontSize: 13
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    }
  },

  filterLanguage: {
    minWidth: 100,
    '& .MuiInputBase-root': {
      fontSize: 13
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    }
  },

  tableWrap: {
    border: '1px solid #c7c7c7',
    borderRadius: 10,
    background: '#fff',
    flex: 1,
    minHeight: 0,
    maxWidth: '100%',
    overflow: 'auto'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff'
  },

  headerRowTable: {
    background: '#c8e1e1'
  },

  th: {
    textAlign: 'left',
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: 'nowrap'
  },

  td: {
    borderTop: '1px solid #ececec',
    padding: '10px 12px',
    fontSize: 13
  },

  emptyTd: {
    padding: 18,
    textAlign: 'center',
    color: '#666'
  },

  shortText: {
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'default'
  },

  hoverBox: {
    position: 'fixed',
    transform: 'translateX(-50%)',
    zIndex: 10000,
    maxWidth: 600,
    maxHeight: 300,
    overflow: 'auto',
    padding: '10px 14px',
    background: 'rgba(0, 0, 0, 0.9)',
    color: '#fff',
    borderRadius: 8,
    fontSize: 13,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    pointerEvents: 'none',
    boxShadow: '0 6px 20px rgba(0,0,0,0.35)'
  },

  editTextarea: {
    minWidth: 220,
    minHeight: 60,
    resize: 'both',
    boxSizing: 'border-box'
  },

  narrowTd: {
    width: 105,
    maxWidth: 105
  },

  smallTd: {
    width: 70,
    maxWidth: 70
  },

  narrowSelect: {
    width: 105,
    boxSizing: 'border-box'
  },

  countrySelectWrap: {
    position: 'relative',
    display: 'inline-block',
    width: 160
  },

  countrySelect: {
    width: 160,
    boxSizing: 'border-box',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    paddingRight: 24
  },

  countrySelectArrow: {
    position: 'absolute',
    right: 7,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    fontSize: 10,
    color: '#222'
  },

  actionSelect: {
    width: 120,
    boxSizing: 'border-box'
  },

  languageSelect: {
    width: 65,
    boxSizing: 'border-box'
  },

  smallInput: {
    width: 60,
    boxSizing: 'border-box'
  }
}

export default AdminPromoBanners